// Mirror of backend services/slug.js so the frontend can build canonical
// place URLs (/:cc/:city/:slug) without a round-trip.

export const COUNTRY_CODE = {
  italy: 'it',
  france: 'fr',
  germany: 'de',
  spain: 'es',
  portugal: 'pt',
  'united kingdom': 'gb',
  uk: 'gb',
  england: 'gb',
  scotland: 'gb',
  ireland: 'ie',
  belgium: 'be',
  netherlands: 'nl',
  holland: 'nl',
  luxembourg: 'lu',
  switzerland: 'ch',
  austria: 'at',
  hungary: 'hu',
  czechia: 'cz',
  'czech republic': 'cz',
  poland: 'pl',
  slovakia: 'sk',
  slovenia: 'si',
  croatia: 'hr',
  greece: 'gr',
  bulgaria: 'bg',
  romania: 'ro',
  serbia: 'rs',
  ukraine: 'ua',
  belarus: 'by',
  russia: 'ru',
  sweden: 'se',
  norway: 'no',
  denmark: 'dk',
  finland: 'fi',
  iceland: 'is',
  estonia: 'ee',
  latvia: 'lv',
  lithuania: 'lt',
  turkey: 'tr',
  cyprus: 'cy',
  malta: 'mt',
  monaco: 'mc',
  'san marino': 'sm',
  vatican: 'va',
  andorra: 'ad',
  gibraltar: 'gi',
  israel: 'il',
  'united states': 'us',
  'united states of america': 'us',
  usa: 'us',
  canada: 'ca',
  // Non-European countries the site now covers — previously these fell back to a
  // slugified name (e.g. "south-africa", truncated "united-arab-"); give them
  // their real ISO code so place URLs are stable.
  'south africa': 'za',
  thailand: 'th',
  china: 'cn',
  botswana: 'bw',
  mexico: 'mx',
  'united arab emirates': 'ae',
  uae: 'ae',
  australia: 'au',
  // Kept in sync with backend scripts/data/countries.json (fallback only — the
  // API now delivers country_code, which buildPlaceUrl prefers).
  albania: 'al',
  'bosnia and herzegovina': 'ba',
  kosovo: 'xk',
  liechtenstein: 'li',
  moldova: 'md',
  montenegro: 'me',
  'north macedonia': 'mk',
  'vatican city': 'va',
};

export function basicSlugify(s) {
  if (!s) return '';
  return String(s)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-')
    .slice(0, 80);
}

export function countryToCode(country) {
  if (!country) return null;
  const norm = country.toLowerCase().trim();
  if (COUNTRY_CODE[norm]) return COUNTRY_CODE[norm];
  return basicSlugify(country).slice(0, 12) || null;
}

export function citySlug(city) { return basicSlugify(city); }
export function placeSlug(name) { return basicSlugify(name); }

// Build the canonical /:cc/:city/:slug URL. Returns null when any piece is
// missing — caller should fall back to /place/:uuid.
export function buildPlaceUrl(est) {
  if (!est) return null;
  const cc = est.country_code || countryToCode(est.country);
  const cs = citySlug(est.city);
  const ps = est.slug || placeSlug(est.name);
  if (!cc || !cs || !ps) return null;
  return `/${cc}/${cs}/${ps}`;
}

// Detect the /:cc/:city/:slug shape in a path. Returns { cc, city, slug } or null.
// Reserved roots (list, place, dashboard, …) are filtered by the caller.
const RESERVED_ROOTS = new Set([
  'list', 'place', 'dashboard', 'favorites', 'privacy', 'terms', 'our-story', 'profile',
  'reset-password', 'verify-email', 'register', 'minyanim', 'api', 'uploads', 'static',
]);

export function parsePlaceSlugPath(parts) {
  if (!parts || parts.length !== 3) return null;
  const [cc, city, slug] = parts;
  if (RESERVED_ROOTS.has(cc)) return null;
  // `cc` is usually an alpha-2 code, but countryToCode() falls back to a
  // slugified country name (up to 12 chars) for countries not in the map — e.g.
  // "south-africa". Accept that range so those places don't 404 on hard refresh.
  if (cc.length < 2 || cc.length > 12) return null;
  if (!/^[a-z0-9-]+$/.test(cc) || !/^[a-z0-9-]+$/.test(city) || !/^[a-z0-9-]+$/.test(slug)) return null;
  return { cc, city, slug };
}
